import { BOOKING_VALID_DURATION } from '../config/constants'

export const STAFF_PENDING_CHECKOUT_STORAGE_KEY = 'malu.staffPendingCheckout'

export type PendingCheckoutCleanupReason = 'session_mount' | 'dialog_close' | 'before_unload' | 'replace_checkout' | 'create_error' | 'complete_error'

export type StaffPendingCheckoutSession = {
  bookingId: string
  createdAt: number
}

const isValidSession = (value: unknown): value is StaffPendingCheckoutSession => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const session = value as StaffPendingCheckoutSession
  return typeof session.bookingId === 'string' && session.bookingId.trim().length > 0 && typeof session.createdAt === 'number'
}

export const readStaffPendingCheckout = (): StaffPendingCheckoutSession | null => {
  try {
    const raw = sessionStorage.getItem(STAFF_PENDING_CHECKOUT_STORAGE_KEY)
    if (!raw) {
      return null
    }

    const parsed: unknown = JSON.parse(raw)
    if (!isValidSession(parsed)) {
      sessionStorage.removeItem(STAFF_PENDING_CHECKOUT_STORAGE_KEY)
      return null
    }

    return { bookingId: parsed.bookingId.trim(), createdAt: parsed.createdAt }
  } catch {
    sessionStorage.removeItem(STAFF_PENDING_CHECKOUT_STORAGE_KEY)
    return null
  }
}

export const saveStaffPendingCheckout = (bookingId: string): void => {
  const id = bookingId.trim()
  if (!id) {
    return
  }

  const session: StaffPendingCheckoutSession = {
    bookingId: id,
    createdAt: Date.now(),
  }

  sessionStorage.setItem(STAFF_PENDING_CHECKOUT_STORAGE_KEY, JSON.stringify(session))
}

export const clearStaffPendingCheckout = (): void => {
  sessionStorage.removeItem(STAFF_PENDING_CHECKOUT_STORAGE_KEY)
}

export const isStaffPendingCheckoutExpired = (session: StaffPendingCheckoutSession): boolean => {
  return Date.now() - session.createdAt > BOOKING_VALID_DURATION * 1000
}

/** Active session only — expired entries are cleared and return null. */
export const getStaffPendingCheckoutIdIfActive = (): string | null => {
  const session = readStaffPendingCheckout()
  if (!session) {
    return null
  }

  if (isStaffPendingCheckoutExpired(session)) {
    clearStaffPendingCheckout()
    return null
  }

  return session.bookingId
}

/** For tab close — includes expired id so unload can still attempt server cleanup. */
export const getStaffPendingCheckoutIdForUnload = (): string | null => {
  return readStaffPendingCheckout()?.bookingId ?? null
}
